import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  lineTotalCents,
  PAYMENT_METHOD_LABELS,
  voidReasonSchema,
  WASTE_REASON_LABELS,
  WASTE_REASONS,
  wasteSchema,
  type PaymentMethod,
  type SaleDTO,
  type TachoDTO,
  type WasteDTO,
  type WasteReason,
  type WasteSummaryDTO,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { HandCoins, PackageMinus, Recycle, Scale, TrendingDown } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { api, errorMessage, isApiError } from '../../lib/api';
import { useProducts } from '../../lib/catalog';
import { formatDateTime, formatKilos, formatMoney } from '../../lib/format';
import { newClientId } from '../../lib/pos';
import { useSession } from '../../lib/session';
import { printSale } from '../../lib/ticket-print';
import { centsToPesosInput, formatQty, pesosToCents, todayIso, toInternalQty } from '../../lib/units';

const firstOfMonth = () => todayIso().slice(0, 8) + '01';

/**
 * Mermas y tacho. Merma es todo lo que no se le vende al cliente y va al tacho:
 * lo que se da de baja (vencido, podrido…), lo que falta al hacer el recuento y lo que se saca al producir.
 * Lo que paga el sebero por el tacho compensa esa pérdida.
 */
export function WastePage() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayIso());
  const [dialog, setDialog] = useState<'waste' | 'tacho' | null>(null);
  const [voiding, setVoiding] = useState<WasteDTO | null>(null);
  const q = useQuery({
    queryKey: ['waste', from, to],
    queryFn: () => api.get<WasteSummaryDTO>(`/waste?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const showMoney = d?.lossCents !== null && d?.lossCents !== undefined;
  const mermaGrams = d ? d.totals.grams + d.count.grams : 0;
  const mermaCost = d && showMoney ? (d.totals.costCents ?? 0) + (d.count.costCents ?? 0) : null;

  return (
    <>
      <PageHeader
        title="Mermas y tacho"
        subtitle="Todo lo que no se le vende al cliente va al tacho: lo que se da de baja, lo que falta en el recuento y lo que se saca al producir. Lo que paga el sebero lo compensa."
        actions={
          <>
            <Button icon={<PackageMinus />} onClick={() => setDialog('waste')}>
              Dar de baja
            </Button>
            <Button variant="primary" icon={<HandCoins />} onClick={() => setDialog('tacho')}>
              Vender el tacho
            </Button>
          </>
        }
      />
      <div className="bf-period">
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Desde <Input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </label>
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Hasta <Input type="date" value={to} min={from} max={todayIso()} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </label>
      </div>
      {!d ? (
        <Skeleton style={{ height: 200 }} />
      ) : (
        <>
          <div className="bf-kpi-grid">
            <Kpi
              label="Merma del período"
              tone="red"
              icon={<PackageMinus />}
              value={formatKilos(mermaGrams)}
              delta={mermaCost !== null ? `Costaba ${formatMoney(mermaCost)}` : 'Bajas + faltante del recuento'}
              deltaMuted
            />
            <Kpi
              label="El tacho devolvió"
              tone="green"
              icon={<Recycle />}
              value={formatMoney(d.tacho.incomeCents)}
              delta={d.tacho.sales ? `${formatKilos(d.tacho.grams)} vendidos al sebero` : 'Todavía no se vendió el tacho'}
              deltaMuted
            />
            {showMoney && (
              <Kpi
                label="Pérdida real"
                tone="amber"
                icon={<TrendingDown />}
                value={formatMoney(Math.max(0, d.lossCents ?? 0))}
                delta={
                  d.salesCents > 0 ? `${(((d.lossCents ?? 0) / d.salesCents) * 100).toFixed(1)} % de lo vendido` : 'Merma − lo que pagó el sebero'
                }
                deltaMuted
              />
            )}
            <Kpi
              label="Sacado al producir y despostar"
              tone="celeste"
              icon={<Scale />}
              value={formatKilos(d.productionGrams)}
              delta="Ya está dentro del costo de lo producido"
              deltaMuted
            />
          </div>

          <Card>
            <CardHeader title="De dónde viene la merma" />
            <CardBody>
              <div className="bf-bar-list">
                {d.byReason.map((r) => (
                  <div key={r.reason} className="bf-pos-row">
                    <span>
                      Bajas · {WASTE_REASON_LABELS[r.reason]} <span className="bf-cell-sub">({r.count})</span>
                    </span>
                    <span className="bf-num">{r.costCents !== null ? formatMoney(r.costCents) : ''}</span>
                  </div>
                ))}
                <div className="bf-pos-row">
                  <span>
                    Faltante en el recuento <span className="bf-cell-sub">({formatKilos(d.count.grams)})</span>
                  </span>
                  <span className="bf-num">{d.count.costCents !== null ? formatMoney(d.count.costCents) : ''}</span>
                </div>
                <p className="bf-cell-sub" style={{ margin: 0 }}>
                  El faltante del recuento es la grasa que se saca en el mostrador, errores de peso… o algo que no se cobró. Se ve al hacer el
                  recuento en Stock.
                </p>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Bajas" subtitle="Lo anulado queda a la vista, tachado." />
            <CardBody flush>
              {d.rows.length === 0 ? (
                <EmptyState icon={<PackageMinus />} title="Sin bajas en el período" />
              ) : (
                <div className="bf-table-wrap">
                  <table className="bf-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Producto</th>
                        <th>Motivo</th>
                        <th style={{ textAlign: 'right' }}>Cantidad</th>
                        {showMoney && <th style={{ textAlign: 'right' }}>Costo</th>}
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {d.rows.map((w) => (
                        <tr key={w.id} style={{ opacity: w.voided ? 0.5 : 1 }}>
                          <td className="bf-cell-sub">{formatDateTime(w.createdAt)}</td>
                          <td>
                            <span className="bf-cell-main">{w.productName}</span> {w.voided && <Badge>Anulada</Badge>}
                            <div className="bf-cell-sub">{[w.notes, w.branchName, w.createdByName].filter(Boolean).join(' · ')}</div>
                          </td>
                          <td>{WASTE_REASON_LABELS[w.reason]}</td>
                          <td className="bf-num" style={{ textAlign: 'right', textDecoration: w.voided ? 'line-through' : undefined }}>
                            {formatQty(w.unitType, w.qty)}
                          </td>
                          {showMoney && (
                            <td
                              className="bf-num bf-text-danger"
                              style={{ textAlign: 'right', textDecoration: w.voided ? 'line-through' : undefined }}
                            >
                              {formatMoney(w.costCents ?? 0)}
                            </td>
                          )}
                          <td style={{ textAlign: 'right' }}>
                            {!w.voided && (
                              <Button size="sm" variant="ghost" onClick={() => setVoiding(w)}>
                                Anular
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
      {dialog === 'waste' && <WasteDialog onClose={() => setDialog(null)} />}
      {dialog === 'tacho' && <TachoDialog onClose={() => setDialog(null)} />}
      {voiding && <VoidWasteDialog waste={voiding} onClose={() => setVoiding(null)} />}
    </>
  );
}

/** Vender el tacho al sebero: se pesa una sola vez, al precio del día o el del mes. Entra a la caja. */
export function TachoDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const tacho = useQuery({ queryKey: ['waste', 'tacho'], queryFn: () => api.get<TachoDTO>('/waste/tacho') });
  const [kg, setKg] = useState('');
  const [mode, setMode] = useState<'kg' | 'total'>('kg');
  const [price, setPrice] = useState<string | null>(null);
  const [total, setTotal] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [buyer, setBuyer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const priceInput = price ?? centsToPesosInput(tacho.data?.priceCents ?? null);
  const grams = toInternalQty('WEIGHT', kg || '0') ?? 0;
  const unit =
    mode === 'kg' ? (pesosToCents(priceInput || '0') ?? 0) : grams > 0 ? Math.round(((pesosToCents(total || '0') ?? 0) * 1000) / grams) : 0;
  const amount = grams > 0 && unit > 0 ? lineTotalCents('WEIGHT', grams, unit) : 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!tacho.data || amount <= 0) return setError('Poné los kilos y el precio.');
    setBusy(true);
    setError(null);
    try {
      const sale = await api.post<SaleDTO>('/sales', {
        clientId: newClientId(),
        items: [{ productId: tacho.data.productId, qty: grams, unitPriceCents: unit }],
        payments: [{ method, amountCents: amount }],
        discountCents: 0,
        notes: buyer.trim() ? `Comprador: ${buyer.trim()}` : null,
      });
      await Promise.all(['waste', 'sales', 'cash', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success('Tacho vendido', `${formatKilos(grams)} por ${formatMoney(amount)}.`);
      printSale(sale);
      onClose();
    } catch (err) {
      setError(
        isApiError(err, 'CASH_CLOSED') ? 'Para registrar la venta abrí la caja en el Punto de venta (la plata entra al cajón).' : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Vender el tacho"
      description="Grasa, hueso y recortes: se pesa todo junto cuando viene el sebero. Entra a la caja como una venta."
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Kilos del tacho">
          <Input className="bf-input--lg" inputMode="decimal" value={kg} onChange={(e) => setKg(e.target.value)} autoFocus placeholder="0,000" />
        </Field>
        <div className="bf-segmented bf-segmented--text" role="group" aria-label="Cómo paga">
          <button type="button" aria-pressed={mode === 'kg'} onClick={() => setMode('kg')}>
            Precio por kilo
          </button>
          <button type="button" aria-pressed={mode === 'total'} onClick={() => setMode('total')}>
            Total que pagó
          </button>
        </div>
        {mode === 'kg' ? (
          <Field
            label="Precio por kilo"
            hint={
              tacho.data?.priceCents
                ? 'Sugerido: el valor del mes (Precios). Cambialo si hoy pagó otro.'
                : 'Lo que paga hoy el sebero. Para un valor fijo del mes, cargalo en Precios.'
            }
          >
            <Input inputMode="decimal" value={priceInput} onChange={(e) => setPrice(e.target.value)} placeholder="$ 0" />
          </Field>
        ) : (
          <Field label="Total que pagó">
            <Input inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="$ 0" />
          </Field>
        )}
        <Field label="Cómo pagó">
          <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {(['CASH', 'TRANSFER', 'QR', 'OTHER'] as const).map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Comprador (opcional)">
          <Input value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Ej.: Grasería Don Pedro" />
        </Field>
        <div className="bf-pos-preview">
          <span>{grams > 0 ? formatKilos(grams) : 'Total'}</span>
          <strong>{formatMoney(amount)}</strong>
        </div>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" icon={<HandCoins />} loading={busy} disabled={amount <= 0}>
            Cobrar {formatMoney(amount)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function WasteDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { me, multiBranch, isHeadquartersUser, selectedBranchId } = useSession();
  const products = useProducts();
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState<WasteReason>('EXPIRED');
  const [notes, setNotes] = useState('');
  const [branchId, setBranchId] = useState(selectedBranchId ?? me.membership.branchId ?? me.branches.find((b) => b.isHeadquarters)?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const list = useMemo(() => (products.data ?? []).filter((p) => p.isActive && p.tracksStock), [products.data]);
  const product = list.find((p) => p.id === productId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = wasteSchema.safeParse({
      productId,
      branchId: multiBranch && isHeadquartersUser ? branchId : null,
      qty: product ? (toInternalQty(product.unitType, qty || '0') ?? 0) : 0,
      reason,
      notes,
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá los datos.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/waste', parsed.data);
      await Promise.all(['waste', 'stock', 'dashboard', 'notifications'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast.success('Baja registrada', 'Va al tacho.');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Dar de baja"
      description="Lo que ya no se puede vender (vencido, podrido, golpeado). Sale del stock con lo que costaba y va al tacho."
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Producto">
          <Select value={productId} onChange={(e) => setProductId(e.target.value)} autoFocus>
            <option value="">Elegí el producto</option>
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={product?.unitType === 'UNIT' ? 'Cantidad (unidades)' : 'Kilos'} hint={product?.unitType === 'UNIT' ? undefined : 'Ej.: 0,750'}>
          <Input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </Field>
        <Field label="Motivo">
          <div className="bf-chips">
            {WASTE_REASONS.map((r) => (
              <button key={r} type="button" className="bf-chip" aria-pressed={reason === r} onClick={() => setReason(r)}>
                {WASTE_REASON_LABELS[r]}
              </button>
            ))}
          </div>
        </Field>
        {multiBranch && isHeadquartersUser && (
          <Field label="Sucursal">
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {me.branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Detalle (opcional)">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej.: se cortó la luz y se perdió la cámara" />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            Dar de baja
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function VoidWasteDialog({ waste, onClose }: { waste: WasteDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    const parsed = voidReasonSchema.safeParse({ reason });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Contá el motivo.');
    setBusy(true);
    try {
      await api.post(`/waste/${waste.id}/void`, parsed.data);
      await Promise.all(['waste', 'stock', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Anular baja"
      description={`${waste.productName}: ${formatQty(waste.unitType, waste.qty)} vuelven al stock.`}
    >
      <div className="bf-form">
        <Field label="Motivo" error={error ?? undefined}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button variant="danger" loading={busy} onClick={() => void submit()}>
            Anular
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
